import React from 'react';
import { BOSS } from '../partials/store/BOSS';
import { InStock } from '../partials/store/InStock';
import { InStockNearby } from '../partials/store/InStockNearby';
import { Clearance } from '../partials/store/Clearance';
import { LimitedAvailability } from '../partials/store/LimitedAvailability';
import { LimitedAvailabilityNearby } from '../partials/store/LimitedAvailabilityNearby';
import { NotLocalized } from '../partials/store/NotLocalized';
import { NoStoreOption } from '../partials/store/NoStoreOption';
import { LiveGoodsPod } from '../partials/store/LiveGoodsPod';
import { BopisOffMessage } from '../partials/store/BopisOffMessage';
import { EmptyStoreMessage } from '../partials/store/EmptyStoreMessage';
import { dataModel } from '../../components/dataModel';
import {
  getLiveGoodsStock,
  getLiveGoodsStoreName,
  isActiveOrInSeason,
  isItemOnClearance,
  isBopisLimitedQty
} from '../../components/helper/utils';
import { FULFILLMENT_METHODS } from '../../components/constants';
import {
  isBOPIS,
  isBOSS,
  isBrowseOnly,
  isBuyInStore,
  isDiscontinued,
  isLimitedStock,
  isInStockYourStore,
  isInStockNearby,
  isLimitedStockNearby,
  isOutOfStockYourStore,
  isAppliance,
  isOutOfStockBoss,
  isOutOfStockOnline,
  getDisplayableInventory,
  getExcludedStateBoss,
  isItemGCC,
  getSellableQuantityYourStore,
  getPickupService,
  getLocalizedStoreStateCode,
  isStateExcluded,
  isClearancePricing,
} from './pod-fulfillment-utils';

const getBopisScenarios = ({
  customRender, isBOSSInStock, product, isNewFulfillmentPodMessagingEnabled, isNarrowPod
}) => {
  if (!isBOPIS(product)) {
    return null;
  }

  if (
    getSellableQuantityYourStore(product) > 0
    && !isOutOfStockYourStore(product)
    && (isBrowseOnly(product) || isBuyInStore(product))
  ) {
    return () => (
      <BopisOffMessage
        product={product}
        customRender={customRender}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        isNarrowPod={isNarrowPod}
      />
    );
  }

  if (getSellableQuantityYourStore(product) === 0 && isBuyInStore(product) && !isLimitedStock(product)) {
    if (!isActiveOrInSeason(product?.fulfillment)) {
      return () => (
        <NoStoreOption
          storeMessage="Unavailable"
          enableOverlayLink={false}
          product={product}
          customRender={customRender}
          isNarrowPod={isNarrowPod}
        />
      );
    }
    return () => (
      <LimitedAvailability product={product} customRender={customRender} bopisNotFulfillableOOS isNarrowPod={isNarrowPod} />
    );
  }

  if (!isBOSSInStock && (isBrowseOnly(product) || isLimitedStock(product))) {
    return () => (
      <LimitedAvailability
        product={product}
        customRender={customRender}
        // Display At your store if BOPIS=false, OOS and either Active or In season
        // Display Limited Stock if BOPIS=false, isLimitedQuantity=true and NOT Active or In season
        bopisNotFulfillableOOS={
          isBuyInStore(product)
          && getSellableQuantityYourStore(product) === 0
          && isActiveOrInSeason(product?.fulfillment)
          && !isLimitedStock(product)
        }
        isNarrowPod={isNarrowPod}
      />
    );
  }

  if (isInStockYourStore(product)) {
    return () => (
      <InStock
        product={product}
        customRender={customRender}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        isNarrowPod={isNarrowPod}
      />
    );
  }
  if (isInStockNearby(product)) {
    return () => (
      <InStockNearby
        product={product}
        customRender={customRender}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        isNarrowPod={isNarrowPod}
      />
    );
  }
  if (isLimitedStockNearby(product) && !isBOSSInStock) {
    return () => <LimitedAvailabilityNearby product={product} customRender={customRender} isNarrowPod={isNarrowPod} />;
  }
  return null;
};

const getBossScenarios = ({
  product, customRender, customer, isNewFulfillmentPodMessagingEnabled, isNarrowPod
}) => {
  if (!isBOSS(product)) {
    return null;
  }
  const excludedState = getExcludedStateBoss(product);
  if (excludedState) {
    return () => (
      <NoStoreOption
        storeMessage="Unavailable in "
        excludedState={excludedState}
        enableOverlayLink
        product={product}
        customRender={customRender}
        isNarrowPod={isNarrowPod}
      />
    );
  }

  if (isOutOfStockBoss(product)) {
    return () => (
      <NoStoreOption
        storeMessage="Unavailable"
        enableOverlayLink={false}
        product={product}
        customRender={customRender}
        isNarrowPod={isNarrowPod}
      />
    );
  }
  return () => (
    <BOSS
      displayQuantity={getDisplayableInventory(product, customer, FULFILLMENT_METHODS.BOSS)}
      customRender={customRender}
      isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      isNarrowPod={isNarrowPod}
    />
  );
};

const getStoreIdScenarios = ({
  customRender, customer, isBOSSInStock, product, isHideLimitedStockClearanceEnabled,
  isNewFulfillmentPodMessagingEnabled, isNarrowPod
}) => {
  if (!getPickupService(product)) {
    const localizedStoreStateCode = getLocalizedStoreStateCode();
    const excludedStateName = isStateExcluded(
      localizedStoreStateCode,
      'boss',
      product?.fulfillment
    );
    if (excludedStateName) {
      return () => (
        <NoStoreOption
          storeMessage="Unavailable in "
          excludedState={excludedStateName}
          enableOverlayLink
          product={product}
          customRender={customRender}
          isNarrowPod={isNarrowPod}
        />
      );
    }
  }

  if (isItemOnClearance(product?.fulfillment) && isClearancePricing(product)) {
    if (isHideLimitedStockClearanceEnabled && isBopisLimitedQty(product?.fulfillment)) {
      return () => (
        <NoStoreOption
          storeMessage="Unavailable at "
          enableOverlayLink
          product={product}
          customRender={customRender}
          isNarrowPod={isNarrowPod}
        />
      );
    }
    return () => <Clearance product={product} customRender={customRender} isNarrowPod={isNarrowPod} />;
  }

  const bopisScenario = getBopisScenarios({
    customRender,
    isBOSSInStock,
    product,
    isNewFulfillmentPodMessagingEnabled,
    isNarrowPod
  });
  if (bopisScenario) {
    return bopisScenario;
  }

  const bossScenario = getBossScenarios({
    product,
    customRender,
    customer,
    isNewFulfillmentPodMessagingEnabled,
    isNarrowPod
  });
  if (bossScenario) {
    return bossScenario;
  }

  /* -----No BOSS or BOPIS----- */
  // @TODO: What is in stock?
  // if (isInStock(product) || hasZeroStock(product) || isLimitedStock(product)) {
  //   /* Not BOPIS but has qtyForSale */
  //   return <LimitedAvailability localizedStore={localizedStore} channel={channel} />;
  // }
  if (isAppliance(product) || isDiscontinued(product)) {
    return () => <EmptyStoreMessage customRender={customRender} product={product} />;
  }
  if (isOutOfStockYourStore(product) || product?.availabilityType?.type === 'Shared') {
    return () => (
      <NoStoreOption
        storeMessage="Unavailable at "
        enableOverlayLink
        product={product}
        customRender={customRender}
        isNarrowPod={isNarrowPod}
      />
    );
  }
  return () => (
    <NoStoreOption
      storeMessage="Unavailable"
      enableOverlayLink={false}
      product={product}
      customRender={customRender}
      isNarrowPod={isNarrowPod}
    />
  );
};

export const getStoreTemplate = ({
  product, storeId, customer, customRender, isHideLimitedStockClearanceEnabled,
  isNewFulfillmentPodMessagingEnabled, isNarrowPod
}) => {
  const isGCC = isItemGCC(product);
  const liveGoodsStock = getLiveGoodsStock(product?.fulfillment);
  if (!product || isGCC) {
    return () => <EmptyStoreMessage isGCC={isGCC} customRender={customRender} product={product} />;
  }

  if (liveGoodsStock === 0) {
    return () => (
      <LiveGoodsPod
        storeName={getLiveGoodsStoreName(product)}
        seasonStatusEligible={product.fulfillment?.seasonStatusEligible}
        customRender={customRender}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
        isNarrowPod={isNarrowPod}
      />
    );
  }

  const isBOSSInStock = isBOSS(product) && !isOutOfStockBoss(product);

  if (storeId) {
    return getStoreIdScenarios({
      customRender,
      customer,
      isBOSSInStock,
      product,
      isHideLimitedStockClearanceEnabled,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod
    });
  }

  return () => <NotLocalized customRender={customRender} />;
};

getStoreTemplate.dataModel = dataModel;
